import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { LoginResponse, usePostExternalLogin } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  FormHandler,
  FormItem,
  SubmitButton,
  TextInput,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { Link } from '@framework/ui';
import { yup } from '@framework/validation';
import { message } from 'antd';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';

const LoginSubmitButton = styled(SubmitButton)`
  width: 100%;
  &.is-front-login {
    font-size: 17.25;
    text-align: center;
    font-weight: 600;
    padding: 0.95em 1em 0.85em;
    min-height: 46px;
    ${up('sm')} {
      &.is-horizontal {
        padding: 7px 15px;
        min-height: 40px;
        margin-top: 8px;
        flex: 1 1 34%;
      }
    }
  }
`;

const LoginSchema = yup
  .object({
    email: yup
      .string()
      .email('Should be a valid email address')
      .required('E-Mail is a required field')
      .label('Email'),
    password: yup
      .string()
      .required('Password is a required field')
      .label('Password'),
  })
  .required()
  .noUnknown();

export interface IExternalUserLoginFormProps {
  onLogin: (result: LoginResponse) => void;
  hideInfos?: boolean;
  submitButtonText?: string;
}

/**
 * Form di login per gli utenti esterni (username+psw)
 */
export function ExternalUserLoginForm(props: IExternalUserLoginFormProps) {
  const { onLogin, hideInfos, submitButtonText } = props;

  const login = usePostExternalLogin();

  const { t } = useTranslation();

  return (
    <FormHandler
      schema={LoginSchema}
      onSubmit={async values => {
        try {
          const result = await login.mutateAsync({
            data: values,
          });

          onLogin(result);
        } catch (e) {
          message.error(getNetworkErrorMessage(e, 'Si è verificato un errore durante il login.')); // prettier-ignore
          console.error(`[LoginForm] Errore durante il login`, { error: e }); // prettier-ignore
        }
      }}
      hideVisualFeedback
    >
      <FormItem
        label={t('user.login.external-user-email', 'Email')}
        name="email"
        required
      >
        <TextInput
          autoFocus
          name="email"
          placeholder={t('user.login.external-user-email', 'Email')}
          prefix={
            <UserOutlined aria-hidden="true" style={{ color: '#757575' }} />
          }
        />
      </FormItem>
      <FormItem
        label={t('user.login.external-user-password', 'Password')}
        name="password"
        required
      >
        <TextInput
          name="password"
          placeholder={t('user.login.external-user-password', 'Password')}
          type="password"
          prefix={
            <LockOutlined aria-hidden="true" style={{ color: '#757575' }} />
          }
        />
      </FormItem>

      <LoginSubmitButton
        type="primary"
        style={{ width: '100%' }}
        pristineMessage={null}
        icon={null}
        className={'is-front-login'}
      >
        {submitButtonText
          ? submitButtonText
          : t('user.login.login-button', 'Accedi')}
      </LoginSubmitButton>

      <div style={{ marginTop: '20px', marginBottom: 0 }}>
        {!hideInfos && (
          <>
            <p>
              {t(
                'user.login.sign-in-question',
                'Non sei ancora registrato sul portale?',
              )}{' '}
              <Link to={'/utente/registrazione' as any}>
                {t(
                  'user.login.sign-in-link',
                  'Vai al modulo di registrazione.',
                )}
              </Link>
            </p>
            <p>
              {t(
                'user.login.password-forgot-question',
                'Hai dimenticato la password?',
              )}{' '}
              <Link to={'/utente/recupera-password' as any}>
                {t('user.login.password-forgot-link', 'Reimposta password.')}
              </Link>
            </p>
          </>
        )}
      </div>
    </FormHandler>
  );
}
